import { RecurrenceInputBlock } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { Component, MutableRefObject, useCallback, useContext, useEffect, useMemo } from 'react';
import { RRule } from 'rrule';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { Colors } from '@shared/styles';
import { MBActionSheet } from '@shared/util/actionSheet';
import { MBAlert } from '@shared/util/alert/index';
import { i18n } from '@shared/util/i18n';
import moment from '@shared/util/moment';
import { getDefaultDateFromRecurrence, matchRecurrenceStartDateWithDate } from './utils';

export function useResetRecurrenceIfNoDateValue(block: RecurrenceInputBlock) {
  const { values, setFieldValue } = useContext(CardFormContext);
  const recurrence = values[block.name];
  const date = values[block.dateFieldName];

  useEffect(() => {
    if (!date && !!recurrence) {
      setFieldValue(block.name, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
}

export function useIsRecurrenceFieldAvailable(block: RecurrenceInputBlock): boolean {
  const { values } = useContext(CardFormContext);
  const date = values[block.dateFieldName];
  return useMemo(() => {
    if (!date) {
      return true;
    }
    return moment(date).isSameOrAfter(moment().startOf('day'));
  }, [date]);
}

export function useResetRecurrenceIfNotAvailable(block: RecurrenceInputBlock) {
  const available = useIsRecurrenceFieldAvailable(block);
  const { values, setFieldValue } = useContext(CardFormContext);
  const recurrence = values[block.name];

  useEffect(() => {
    if (!available && !!recurrence) {
      setFieldValue(block.name, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, recurrence]);
}

export function useUpdateRecurrence(
  block: RecurrenceInputBlock,
): (rule: RRule | undefined) => void {
  const { values, setFieldValue } = useContext(CardFormContext);
  const batchByAssignee = values.batchByAssignee;

  return useCallback(
    async (rule: RRule | undefined) => {
      if (!rule) {
        setFieldValue(block.name, undefined);
        return;
      }

      if (batchByAssignee) {
        const confirmed = await MBAlert.confirm({
          title: i18n('card.blocks.recurrenceInput.batchByAssigneeAlert.title'),
          message: i18n('card.blocks.recurrenceInput.batchByAssigneeAlert.message'),
          confirmText: i18n('card.blocks.recurrenceInput.batchByAssigneeAlert.confirmText'),
        });
        if (!confirmed) {
          return;
        }
        setFieldValue('batchByAssignee', false);
      }

      const ruleMatchingDate = matchRecurrenceStartDateWithDate(
        rule,
        !isEmpty(values[block.dateFieldName]) ? new Date(values[block.dateFieldName]) : new Date(),
      );
      const newRule = ruleMatchingDate.toString();
      setFieldValue(block.name, newRule);
      if (!isNil(block.dateFieldName)) {
        setFieldValue(block.dateFieldName, getDefaultDateFromRecurrence(newRule));
      }
    },
    [batchByAssignee, values, block.dateFieldName, block.name, setFieldValue],
  );
}

export type RecurrenceScope = 'ENTITY' | 'ENTITY_UPCOMING';

export async function requestRecurrenceScope(containerRef: Component): Promise<RecurrenceScope> {
  return new Promise((resolve, reject) => {
    MBActionSheet.show({
      parent: containerRef,
      title: i18n('card.update.recurringConfirmation.message'),
      onCancel: reject,
      webPosition: () => ({
        flex: 1,
        backgroundColor: Colors.overlay.black30,
        alignItems: 'center',
        justifyContent: 'center',
      }),
      options: [
        {
          title: i18n('card.update.recurringConfirmation.currentOnly'),
          onPress: () => resolve('ENTITY'),
        },
        {
          title: i18n('card.update.recurringConfirmation.currentAndUpcoming'),
          onPress: () => resolve('ENTITY_UPCOMING'),
        },
      ],
    });
  });
}

export function usePresubmitRecurrenceUpdateScopePrompt(
  block: RecurrenceInputBlock,
  containerRef: MutableRefObject<MBTouchableOpacity>,
) {
  const { values, registerPresubmitHook, unregisterPresubmitHook } = useContext(CardFormContext);
  const date = values[block.dateFieldName];
  const available = useIsRecurrenceFieldAvailable(block);

  // Ask how to update recurrence if (1) recurrence
  // is updated, or (2) recurrence is removed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prevRecurrence = useMemo<string>(() => values[block.name], []);
  const nextRecurrence = values[block.name] as string;
  const getRecurrenceUpdateScope = useCallback(async () => {
    // If editing a card without recurrence
    if (!available || (!prevRecurrence && !nextRecurrence)) {
      return 'ENTITY';
    }
    // If adding or removing a recurrence
    if (!!prevRecurrence !== !!nextRecurrence) {
      return 'ENTITY_UPCOMING';
    }

    return requestRecurrenceScope(containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, prevRecurrence, nextRecurrence, containerRef]);

  useEffect(() => {
    registerPresubmitHook(block.name, async () => {
      // Note(fant): this value is magically used in
      // scenes/cards/form/update/container
      const recurrenceUpdateScope = await getRecurrenceUpdateScope();
      return { recurrenceUpdateScope };
    });
    return () => unregisterPresubmitHook(block.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.name, getRecurrenceUpdateScope]);
}
